/** Single-campaign detail — tabbed layout (Campaigns/Sessions v2 UX pass).
 *
 * Header carries the name + status pills + the primary actions (Report,
 * ✏️ Edit, Archive, Remove). The body is split into tabs so the page is no
 * longer one long confusing scroll:
 *   • Overview      — funnel donut + launch status/button.
 *   • Beneficiaries — the leads list; each beneficiary expands to its
 *                     sessions (open now / reschedule / send reminder).
 *   • Operators     — per-operator stats + drill-down AND add/remove
 *                     assignment in one place.
 *   • Questions     — inspection questions.
 *   • Settings      — booking-wave config (type + slot rhythm), template
 *                     overrides, and the end/reopen + delete danger zone.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { formatDateTime } from "@/lib/format";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { NotifyPolicyEditor } from "@/modules/notifications/NotifyPolicyEditor";

import { WaveScheduleEditor } from "./WaveScheduleEditor";
import { useWaveSummary } from "./useWaveSummary";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";

import { CampaignFunnelBars } from "@/components/CampaignFunnelBars";
import { PermissionGate } from "@/components/PermissionGate";
import { OperatorAvailabilityBoard } from "@/modules/admin";
import { LiveInspectionsBoard } from "@/modules/sessions/LiveInspectionsBoard";
import { SessionsTable } from "@/modules/sessions/SessionsTable";
import { ApiPaths, api } from "@/lib/api";
import type { Campaign } from "@/lib/types";

import { deleteCampaign, patchCampaign } from "./api";
import { AddAssigneeModal } from "./AddAssigneeModal";
import { listAssignees } from "./assigneesApi";
import { operatorFigures, operatorInitials } from "./operatorFigures";
import { Figure, Meter } from "./OperatorCardParts";
import { CampaignLeads } from "./CampaignLeads";
import { CampaignNotificationTemplatesCard } from "./CampaignNotificationTemplatesCard";
import { CampaignQuestions } from "./CampaignQuestions";
import { ConformityCard } from "./ConformityCard";
import { CampaignVerdict } from "./CampaignVerdict";
import { CampaignProgressBands } from "./CampaignProgressBands";
import { computeGoal } from "./campaignGoal";
import { CampaignActionQueue } from "./CampaignActionQueue";
import { CreatedVsClosedChart } from "./CreatedVsClosedChart";
import { CampaignOperatorPanel } from "./CampaignOperatorPanel";
import { RemoveOperatorModal } from "./RemoveOperatorModal";
import { OperatorBeneficiaries } from "./OperatorBeneficiaries";
import { CustomFieldsCard } from "@/modules/admin";
import { SlotSettingsStep } from "./SlotSettingsStep";
import { DEFAULT_SLOT_SETTINGS, type SlotSettings } from "./slots";

type Tab =
  | "overview"
  | "beneficiaries"
  | "sessions"
  | "operators"
  | "questions"
  | "fields"
  | "settings";
const TABS: Tab[] = [
  "overview",
  "beneficiaries",
  "sessions",
  "operators",
  "questions",
  "fields",
  "settings",
];

type LaunchSummary = {
  launched_at: string | null;
  operator_count: number;
  daily_capacity: number;
  daily_send_target: number;
  pending_leads: number;
  capacity_next_7d: { date: string; free: number }[];
};

export function CampaignDetail() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();

  // The tab lives in the URL, not just in state. Ending an inspection
  // returns the operator to the beneficiary list of the campaign it
  // belonged to, which is only addressable if the tab is. It also means
  // a refresh or a back-button keeps your place instead of snapping to
  // Overview.
  const [searchParams, setSearchParams] = useSearchParams();
  const urlTab = searchParams.get("tab") as Tab | null;
  const tab: Tab = urlTab && TABS.includes(urlTab) ? urlTab : "overview";
  const setTab = (next: Tab) => {
    const p = new URLSearchParams(searchParams);
    if (next === "overview") p.delete("tab");
    else p.set("tab", next);
    // replace: switching tabs should not stack history entries the
    // back button then has to walk through.
    setSearchParams(p, { replace: true });
  };

  const campaign = useQuery({
    queryKey: ["campaign", id],
    queryFn: async () => (await api.get<Campaign>(ApiPaths.campaign(id!))).data,
    enabled: !!id,
  });

  const breakdown = useQuery({
    queryKey: ["campaign-status-breakdown", id],
    queryFn: async () =>
      (
        await api.get<{
          total: number;
          pending: number;
          scheduled: number;
          made: number;
          rescheduled: number;
        }>(ApiPaths.campaignStatusBreakdown(id!))
      ).data,
    enabled: !!id,
    retry: false,
  });

  const launchInfo = useQuery({
    queryKey: ["campaign-launch", id],
    queryFn: async () =>
      (await api.get<LaunchSummary>(ApiPaths.campaignLaunch(id!))).data,
    enabled: !!id,
    retry: false,
  });
  const patch = useMutation({
    mutationFn: (payload: Parameters<typeof patchCampaign>[1]) =>
      patchCampaign(id!, payload),
    onSuccess: (c) => {
      qc.setQueryData(["campaign", id], c);
      void qc.invalidateQueries({ queryKey: ["campaigns"] });
    },
  });

  const remove = useMutation({
    mutationFn: () => deleteCampaign(id!),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["campaigns"] });
      navigate("/campaigns", { replace: true });
    },
  });

  if (campaign.isLoading) {
    return (
      <p className="text-sm text-nb-text-2">{t("common.loading")}</p>
    );
  }
  if (!campaign.data) {
    return (
      <p className="card text-sm text-nb-text-2">
        {t("campaigns.detail.notFound")}
      </p>
    );
  }
  const c = campaign.data;

  return (
    <div className="space-y-4">
      {/* ---- Header + action bar (points #4, #5) ---- */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <Link
            to="/campaigns"
            className="text-xs text-nb-text-3 hover:text-nb-text-2"
          >
            {t("campaignsPage.title")}
          </Link>
          <h1 className="flex items-center gap-2 text-2xl font-semibold">
            <span className="truncate">{c.name}</span>
            {!c.launched_at && (
              <span className="pill bg-slate-700/40 text-xs text-nb-text-2">
                {t("campaigns.detail.draft")}
              </span>
            )}
            {c.launched_at && !c.is_ended && !c.is_archived && (
              <span className="pill bg-nb-mint/15 text-xs text-nb-mint">
                {t("campaigns.detail.active")}
              </span>
            )}
            {c.is_ended && (
              <span className="pill bg-emerald-500/15 text-xs text-emerald-300">
                {t("campaigns.detail.ended")}
              </span>
            )}
            {c.is_archived && (
              <span className="pill bg-slate-700/40 text-xs text-nb-text-2">
                {t("campaigns.detail.archived")}
              </span>
            )}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          {c.campaign_kind === "ai_assisted" && (
            <Link to={`/campaigns/${c.id}/ai`} className="btn-primary text-sm">
              {t("aiCampaign.openConsole", "AI Console")}
            </Link>
          )}
          {c.launched_at && (
            <Link
              to={`/campaigns/${c.id}/report`}
              className="btn-primary text-sm"
            >
              {t("leads.report", "Report")}
            </Link>
          )}
          <Link to={`/calendar?campaign=${c.id}`} className="btn-ghost text-sm">
            {t("campaigns.detail.viewCalendar")}
          </Link>
          <PermissionGate perm="campaign.update">
            <button
              type="button"
              className="btn-ghost text-sm"
              title={t("campaigns.detail.editDetails")}
              onClick={() => navigate(`/campaigns/${c.id}/wizard`)}
            >
              ✏️ {t("common.edit")}
            </button>
            <button
              type="button"
              className="btn-ghost text-sm"
              onClick={() => patch.mutate({ archived: !c.is_archived })}
              disabled={patch.isPending}
            >
              {c.is_archived
                ? t("campaigns.detail.unarchive")
                : t("campaigns.detail.archive")}
            </button>
          </PermissionGate>
          <PermissionGate perm="campaign.delete">
            <button
              type="button"
              className="btn-ghost text-sm text-cvp-danger hover:bg-cvp-danger/10"
              onClick={() => {
                if (
                  confirm(
                    t("campaigns.detail.deleteConfirm", {
                      name: c.name,
                      count: c.session_count,
                    }),
                  )
                ) {
                  remove.mutate();
                }
              }}
              disabled={remove.isPending}
            >
              {t("common.remove", "Remove")}
            </button>
          </PermissionGate>
        </div>
      </div>

      {/* ---- Tab bar ---- */}
      <div className="flex flex-wrap gap-1 border-b border-nb-border">
        {TABS.map((k) => (
          <button
            key={k}
            type="button"
            onClick={() => setTab(k)}
            className={
              "-mb-px border-b-2 px-3 py-2 text-sm " +
              (tab === k
                ? "border-nb-mint font-medium text-nb-text"
                : "border-transparent text-nb-text-2 hover:text-nb-text")
            }
          >
            {t(`campaigns.detail.tab_${k}`)}
          </button>
        ))}
      </div>

      {/* ---- Tab content ---- */}
      {tab === "overview" && (
        <OverviewTab
          c={c}
          breakdown={breakdown.data}
          launchInfo={launchInfo.data}
          onEdit={() => navigate(`/campaigns/${c.id}/wizard`)}
        />
      )}

      {tab === "beneficiaries" && (
        <PermissionGate perm="campaign.read">
          <CampaignLeads campaignId={c.id} campaignName={c.name} />
        </PermissionGate>
      )}

      {tab === "sessions" && (
        <PermissionGate perm="session.read">
          <SessionsTable scope={{ campaignId: c.id }} />
        </PermissionGate>
      )}

      {tab === "operators" && (
        <OperatorsTab campaignId={c.id} />
      )}

      {tab === "questions" && (
        <PermissionGate perm="campaign.read">
          <CampaignQuestions campaignId={c.id} />
        </PermissionGate>
      )}

      {tab === "fields" && (
        <PermissionGate perm="campaign.read">
          <CustomFieldsCard campaign={c.id} />
        </PermissionGate>
      )}

      {tab === "settings" && (
        <SettingsTab
          c={c}
          onPatch={(p) => patch.mutate(p)}
          patching={patch.isPending}
        />
      )}

    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Overview tab                                                       */
/* ------------------------------------------------------------------ */

function OverviewTab({
  c,
  breakdown,
  launchInfo,
  onEdit,
}: {
  c: Campaign;
  breakdown?: {
    total: number;
    pending: number;
    scheduled: number;
    made: number;
    rescheduled: number;
    beneficiaries?: number;
    sites?: number;
    conform?: number;
    non_conform?: number;
    conformity_pending?: number;
    no_show?: number;
    declined?: number;
    never_invited?: number;
    sessions?: {
      total: number;
      created: number;
      closed: number;
      cancelled: number;
    };
    closed_by_day?: Array<{ date: string; closed: number }>;
    created_by_day?: Array<{ date: string; created: number }>;
    end_date?: string | null;
  };
  launchInfo?: LaunchSummary;
  onEdit: () => void;
}) {
  const { t } = useTranslation();
  const isAi = c.campaign_kind === "ai_assisted";
  // The overview's "invitations send automatically ..." sentence, built
  // from the stored schedule rather than a hardcoded hour. Shares the
  // editor's query key, so the two can never disagree.
  const waveSummary = useWaveSummary({ kind: "campaign", id: c.id });

  const sendWave = useMutation({
    mutationFn: async () =>
      (
        await api.post<{ queued: boolean; estimated: number }>(
          ApiPaths.campaignSendWave(c.id),
          {},
        )
      ).data,
  });
  // "What have we achieved, and what will the goal take?" — the two
  // questions the overview is opened to answer (asked 2026-09-27).
  // Guarded on `total`, not merely on `breakdown` being truthy. The
  // endpoint can answer with a partial object — the E2E backend returns
  // one — and a page that crashes is far worse than a card that is
  // missing, so this block earns its place only with real numbers.
  const goal = breakdown?.total
    ? computeGoal({
        total: breakdown.total,
        made: breakdown.made,
        closedByDay: breakdown.closed_by_day ?? [],
        startDate: c.start_date ?? null,
        endDate: breakdown.end_date ?? c.end_date ?? null,
        today: new Date(),
        dailyCapacity: launchInfo?.daily_capacity,
      })
    : null;

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      {goal && breakdown?.total ? (
        <div className="grid items-start gap-4 lg:col-span-2 lg:grid-cols-[19rem_1fr]">
          {/* The rail carries the campaign's vitals, stacked, so they sit
              BESIDE the bands rather than pushing them down the page. */}
          <div className="flex flex-col gap-4">
            <CampaignVerdict
              goal={goal}
              capacityPerDay={launchInfo?.daily_capacity}
            />
            {breakdown && breakdown.total > 0 && (
            <section className="card space-y-3">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-nb-text-2">
                {t("campaigns.detail.funnelTitle")}
                <span className="ml-2 font-mono text-nb-text-3">
                  {breakdown.total.toLocaleString()}
                </span>
              </h2>
              <CampaignFunnelBars data={breakdown} />

              {/* Requested 2026-09-23 — "in the circle graph we've to show
                  how many total (beneficiary) and how many total sites."
                  A beneficiary with four parcels is four inspections worth
                  of work, so the beneficiary count alone understates the
                  campaign. */}
              {(breakdown.sites ?? 0) > 0 && (
                <dl className="grid grid-cols-2 gap-2 border-t border-nb-border pt-3 text-center">
                  <div>
                    <dt className="text-[10px] uppercase tracking-wide text-nb-text-3">
                      {t("campaigns.detail.beneficiariesTotal", "Beneficiaries")}
                    </dt>
                    <dd className="text-lg font-semibold text-nb-text">
                      {(breakdown.beneficiaries ?? breakdown.total).toLocaleString()}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-[10px] uppercase tracking-wide text-nb-text-3">
                      {t("campaigns.detail.sitesTotal", "Sites")}
                    </dt>
                    <dd className="text-lg font-semibold text-nb-mint">
                      {(breakdown.sites ?? 0).toLocaleString()}
                    </dd>
                  </div>
                </dl>
              )}
            </section>
          )}
            {breakdown &&
            (breakdown.conform ?? 0) +
              (breakdown.non_conform ?? 0) +
              (breakdown.conformity_pending ?? 0) >
              0 && (
              <ConformityCard
                conform={breakdown.conform ?? 0}
                nonConform={breakdown.non_conform ?? 0}
                pending={breakdown.conformity_pending ?? 0}
              />
            )}
            {/* One card, not two. Load + presence for the same people
                used to sit in adjacent boxes, which hid that the
                operator holding 2% was the one with no schedule. */}
            <CampaignOperatorPanel
              campaignId={c.id}
              capacityPerDay={launchInfo?.daily_capacity}
              bookedAhead={breakdown.scheduled}
            />
          </div>

          <div className="flex flex-col gap-4">
            <CampaignProgressBands
              breakdown={breakdown}
              goal={goal}
              capacityPerDay={launchInfo?.daily_capacity}
            />
            <CampaignActionQueue
              campaignId={c.id}
              total={breakdown.total}
              neverInvited={breakdown.never_invited ?? 0}
              noShow={breakdown.no_show ?? 0}
              conform={breakdown.conform ?? 0}
              nonConform={breakdown.non_conform ?? 0}
              conformityPending={breakdown.conformity_pending ?? 0}
              sessions={breakdown.sessions}
            />
            <CreatedVsClosedChart
              createdByDay={breakdown.created_by_day ?? []}
              closedByDay={breakdown.closed_by_day ?? []}
              goalTotal={breakdown.total}
              startDate={c.start_date ?? null}
              endDate={breakdown.end_date ?? c.end_date ?? null}
              today={new Date()}
            />
          </div>
        </div>
      ) : (
        <>
          <OperatorAvailabilityBoard />
          {breakdown && breakdown.total > 0 && (
          <section className="card space-y-3">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-nb-text-2">
              {t("campaigns.detail.funnelTitle")}
              <span className="ml-2 font-mono text-nb-text-3">
                {breakdown.total.toLocaleString()}
              </span>
            </h2>
            <CampaignFunnelBars data={breakdown} />

            {/* Requested 2026-09-23 — "in the circle graph we've to show
                how many total (beneficiary) and how many total sites."
                A beneficiary with four parcels is four inspections worth
                of work, so the beneficiary count alone understates the
                campaign. */}
            {(breakdown.sites ?? 0) > 0 && (
              <dl className="grid grid-cols-2 gap-2 border-t border-nb-border pt-3 text-center">
                <div>
                  <dt className="text-[10px] uppercase tracking-wide text-nb-text-3">
                    {t("campaigns.detail.beneficiariesTotal", "Beneficiaries")}
                  </dt>
                  <dd className="text-lg font-semibold text-nb-text">
                    {(breakdown.beneficiaries ?? breakdown.total).toLocaleString()}
                  </dd>
                </div>
                <div>
                  <dt className="text-[10px] uppercase tracking-wide text-nb-text-3">
                    {t("campaigns.detail.sitesTotal", "Sites")}
                  </dt>
                  <dd className="text-lg font-semibold text-nb-mint">
                    {(breakdown.sites ?? 0).toLocaleString()}
                  </dd>
                </div>
              </dl>
            )}
          </section>
        )}
          {breakdown &&
          (breakdown.conform ?? 0) +
            (breakdown.non_conform ?? 0) +
            (breakdown.conformity_pending ?? 0) >
            0 && (
            <ConformityCard
              conform={breakdown.conform ?? 0}
              nonConform={breakdown.non_conform ?? 0}
              pending={breakdown.conformity_pending ?? 0}
            />
          )}
        </>
      )}
      <LiveInspectionsBoard campaignId={c.id} />

      <section className="card space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-nb-text-2">
            {t("campaigns.detail.bookingWave")}
          </h2>
          {c.launched_at ? (
            <span className="text-xs text-nb-mint">
              {t("campaigns.detail.activeSince", {
                date: formatDateTime(c.launched_at),
              })}
            </span>
          ) : (
            <span className="text-xs text-nb-text-3">
              {t("campaigns.detail.notLaunched")}
            </span>
          )}
        </div>
        {launchInfo && (
          <div className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
            <Stat label={t("campaigns.detail.operators")} value={launchInfo.operator_count} />
            <Stat label={t("campaigns.detail.slotsDay")} value={launchInfo.daily_capacity} />
            <Stat label={t("campaigns.detail.sendDay")} value={launchInfo.daily_send_target} />
            <Stat label={t("campaigns.detail.pendingLeads")} value={launchInfo.pending_leads} />
          </div>
        )}
        {c.progress !== null && (
          <div className="space-y-1.5">
            <div className="flex items-baseline justify-between text-[10px] uppercase tracking-wider text-nb-text-3">
              <span>
                {c.start_date && c.end_date
                  ? t("campaigns.detail.progressWindow")
                  : t("campaigns.detail.progressSessions")}
              </span>
              <span className="font-mono text-nb-text">
                {Math.round((c.progress ?? 0) * 100)}%
              </span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-nb-surface">
              <div
                className={
                  "h-full rounded-full transition-all " +
                  (c.is_ended ? "bg-emerald-500" : "bg-nb-mint")
                }
                style={{ width: `${Math.round((c.progress ?? 0) * 100)}%` }}
              />
            </div>
          </div>
        )}
        <PermissionGate perm="campaign.launch">
          {c.launched_at ? (
            // Active — the invitation wave runs on its own; no manual
            // "launch" step. Explain the automation, and offer an on-demand
            // "send a wave now" for operators who don't want to wait.
            //
            // The sentence quotes the schedule actually stored. It used to
            // hardcode "every weekday at 10:00", which became wrong the
            // moment the schedule was configurable.
            <div className="space-y-2">
              <p className="rounded-md border border-nb-mint/25 bg-nb-mint/5 p-2.5 text-xs text-nb-text-2">
                {waveSummary.schedule
                  ? t("campaigns.detail.activeAuto", {
                      schedule: waveSummary.schedule,
                      zone: waveSummary.zone,
                    })
                  : // Still loading: say it is automatic without naming an
                    // hour. A vague sentence beats a confidently wrong one.
                    t("campaigns.detail.activeAutoGeneric")}
              </p>
              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  className="btn-ghost text-sm"
                  disabled={sendWave.isPending}
                  onClick={() => {
                    const msg = isAi
                      ? t(
                          "campaigns.detail.sendWaveConfirmAi",
                          "Call this campaign's pending contacts now? This places AI voice calls immediately, on top of the automatic sequence.",
                        )
                      : t("campaigns.detail.sendWaveConfirm");
                    if (confirm(msg)) {
                      sendWave.mutate();
                    }
                  }}
                >
                  {sendWave.isPending
                    ? t("campaigns.detail.sendingWave")
                    : isAi
                      ? t("campaigns.detail.sendWaveNowAi", "Call a wave now")
                      : t("campaigns.detail.sendWaveNow")}
                </button>
                {sendWave.isSuccess && (
                  <span className="text-xs text-nb-mint">
                    {isAi
                      ? t("campaigns.detail.sendWaveQueuedAi", {
                          count: sendWave.data?.estimated ?? 0,
                          defaultValue:
                            "Calling up to {{count}} contact(s) now — dialling shortly.",
                        })
                      : t("campaigns.detail.sendWaveQueued", {
                          count: sendWave.data?.estimated ?? 0,
                        })}
                  </span>
                )}
                {sendWave.isError && (
                  <span className="text-xs text-cvp-danger">
                    {(sendWave.error as Error)?.message ??
                      t("campaigns.detail.sendWaveError")}
                  </span>
                )}
              </div>
              <p className="text-xs text-nb-text-3">
                {t("campaigns.detail.sendWaveHelp")}
              </p>
            </div>
          ) : (
            // Draft — activation happens in the setup wizard.
            <div className="space-y-1.5">
              <button
                type="button"
                className="btn-primary text-sm"
                onClick={onEdit}
              >
                {t("campaigns.detail.activateCta")}
              </button>
              <p className="text-xs text-nb-text-3">
                {t("campaigns.detail.activateHelp")}
              </p>
            </div>
          )}
        </PermissionGate>
      </section>
    </div>
  );
}

function Stat({
  label,
  value,
}: {
  label: string;
  value: number | string;
}) {
  return (
    <div className="min-w-[6.5rem] rounded-lg border border-nb-border bg-nb-surface px-3 py-2">
      <div className="text-[10px] uppercase tracking-wide text-nb-text-3">
        {label}
      </div>
      <div className="font-mono text-lg tabular-nums">{value}</div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Operators tab — stats + drill-down + assignment (points #2, #8)    */
/* ------------------------------------------------------------------ */

/** One unified Operators section: assignment (add/remove + visibility) AND
 * per-operator stats + drill-down in a single list — they're the same
 * operators, so no more two-cards confusion. */
function OperatorsTab({ campaignId }: { campaignId: string }) {
  const { t } = useTranslation();
  const [showAdd, setShowAdd] = useState(false);

  const operatorStats = useQuery({
    queryKey: ["campaign-operator-stats", campaignId],
    queryFn: async () =>
      (
        await api.get<{ operator_count: number; operators: OperatorStat[] }>(
          ApiPaths.campaignOperatorStats(campaignId),
        )
      ).data,
    retry: false,
  });
  const assignees = useQuery({
    queryKey: ["campaign-assignees", campaignId],
    queryFn: () => listAssignees(campaignId),
  });
  // Removal goes through the handover dialog, exactly as it does on the
  // overview rail. A direct DELETE now answers 409 whenever the operator
  // still holds unrun work — by design, so nothing gets orphaned — which
  // would make this tab's Remove look broken.
  const [removing, setRemoving] = useState<{ id: number; name: string } | null>(
    null,
  );

  const assigneeIds = new Set((assignees.data ?? []).map((a) => a.user_id));
  const assignedCount = assignees.data?.length ?? 0;
  const ops = operatorStats.data?.operators ?? [];

  const totalBooked = ops.reduce((s, o) => s + o.booked, 0);
  // Summed from the real bucket, not from `booked - upcoming`. The
  // same subtraction that claimed 475 done per operator was also
  // inflating this campaign-level total.
  const totalHandled = ops.reduce((s, o) => s + (o.done ?? 0), 0);
  const totalAwaiting = ops.reduce((s, o) => s + (o.awaiting ?? 0), 0);
  const totalUpcoming = ops.reduce((s, o) => s + o.upcoming, 0);
  // Against work that actually reached a slot. Dividing by everything
  // assigned would drag it toward zero purely because invitations are
  // still out.
  const reachedSlot = Math.max(0, totalBooked - totalAwaiting);
  const completionRate =
    reachedSlot > 0 ? Math.round((totalHandled / reachedSlot) * 100) : 0;

  return (
    <section className="card space-y-3">
      <header className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold uppercase tracking-wide text-nb-text-2">
            {t("campaigns.detail.operatorsCount", {
              count: assignedCount || ops.length,
            })}
          </h2>
          <p className="text-xs text-nb-text-3">
            {assignedCount === 0
              ? t("campaigns.detail.operatorsOpenToAll")
              : t("campaigns.detail.operatorsAssignedNote", {
                  count: assignedCount,
                })}
          </p>
        </div>
        <PermissionGate perm="campaign.members.manage">
          <button
            type="button"
            className="btn-ghost text-sm"
            onClick={() => setShowAdd(true)}
          >
            {t("campaigns.detail.addOperator")}
          </button>
        </PermissionGate>
      </header>

      {ops.length > 0 && (
        <>
          {/* Campaign totals as plain labelled figures.
              The donut that used to sit here drew a 98/2 split as an
              almost-closed ring with a sliver — the largest element on the
              page carrying the least information, beside a legend that
              already gave both numbers. "Total operators" also just
              repeated the heading. */}
          <div className="flex flex-wrap gap-2">
            <Stat
              label={t("campaigns.detail.sessionsTotal", "Total sessions")}
              value={totalBooked}
            />
            <Stat
              label={t("campaigns.detail.opDone")}
              value={totalHandled}
            />
            <Stat
              label={t("campaigns.detail.opAwaiting")}
              value={totalAwaiting}
            />
            <Stat
              label={t("campaigns.detail.colUpcoming")}
              value={totalUpcoming}
            />
            <Stat
              label={t("campaigns.detail.completionRate", "Completion")}
              value={`${completionRate}%`}
            />
          </div>

          <ul className="grid gap-2.5 sm:grid-cols-2">
            {ops.map((o) => (
              <OperatorCard
                key={o.user_id}
                campaignId={campaignId}
                op={o}
                totalBooked={totalBooked}
                onRemove={
                  assigneeIds.has(o.user_id)
                    ? () =>
                        setRemoving({
                          id: o.user_id,
                          name: o.display_name,
                        })
                    : undefined
                }
              />
            ))}
          </ul>
        </>
      )}

      {removing && (
        <RemoveOperatorModal
          campaignId={campaignId}
          userId={removing.id}
          displayName={removing.name}
          candidates={ops
            .filter(
              (o) =>
                o.user_id !== removing.id &&
                (assignees.data ?? []).some(
                  (a) => a.user_id === o.user_id && a.is_bookable !== false,
                ),
            )
            .map((o) => ({ user_id: o.user_id, display_name: o.display_name }))}
          onClose={() => setRemoving(null)}
        />
      )}

      {showAdd && (
        <AddAssigneeModal
          campaignId={campaignId}
          existing={assigneeIds}
          onClose={() => setShowAdd(false)}
        />
      )}
    </section>
  );
}

function SettingsTab({
  c,
  onPatch,
  patching,
}: {
  c: Campaign;
  onPatch: (p: Parameters<typeof patchCampaign>[1]) => void;
  patching: boolean;
}) {
  const { t } = useTranslation();
  const [slots, setSlots] = useState<SlotSettings>(DEFAULT_SLOT_SETTINGS);
  const [seeded, setSeeded] = useState(false);
  useEffect(() => {
    if (seeded) return;
    setSlots({
      slot_minutes: c.slot_minutes ?? DEFAULT_SLOT_SETTINGS.slot_minutes,
      break_minutes: c.break_minutes ?? DEFAULT_SLOT_SETTINGS.break_minutes,
      slack_minutes: c.slack_minutes ?? DEFAULT_SLOT_SETTINGS.slack_minutes,
      break_mode:
        (c.break_mode as SlotSettings["break_mode"]) ??
        DEFAULT_SLOT_SETTINGS.break_mode,
      calls_before_break:
        c.calls_before_break ?? DEFAULT_SLOT_SETTINGS.calls_before_break,
      minutes_before_break:
        c.minutes_before_break ?? DEFAULT_SLOT_SETTINGS.minutes_before_break,
      long_break_minutes:
        c.long_break_minutes ?? DEFAULT_SLOT_SETTINGS.long_break_minutes,
    });
    setSeeded(true);
  }, [c, seeded]);

  return (
    <div className="space-y-4">
      <PermissionGate perm="campaign.update">
        <section className="card space-y-4">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-nb-text-2">
            {t("campaigns.detail.bookingWaveConfig")}
          </h2>
          <label className="flex items-center gap-2 text-sm text-nb-text-2">
            <span>{t("campaigns.detail.type")}</span>
            <select
              className="input text-sm"
              value={c.campaign_type ?? "standard"}
              onChange={(e) => onPatch({ campaign_type: e.target.value })}
            >
              <option value="standard">{t("campaigns.detail.typeStandard")}</option>
              <option value="public_sector">
                {t("campaigns.detail.typePublicSector")}
              </option>
            </select>
          </label>
          <SlotSettingsStep
            campaignId={c.id}
            value={slots}
            onChange={(patch) => setSlots((s) => ({ ...s, ...patch }))}
          />
          <div>
            <button
              type="button"
              className="btn-primary text-sm"
              disabled={patching}
              onClick={() => onPatch(slots)}
            >
              {patching ? t("common.saving") : t("campaigns.detail.saveSlots")}
            </button>
          </div>
        </section>
      </PermissionGate>

      <PermissionGate perm="campaign.read">
        <CampaignNotificationTemplatesCard campaignId={c.id} />
      </PermissionGate>

      <PermissionGate perm="campaign.update">
        <section className="card space-y-2">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-nb-text-2">
            {t("campaigns.detail.statusZone")}
          </h2>
          <button
            type="button"
            className="btn-ghost text-sm"
            onClick={() => {
              const msg = c.is_ended
                ? t("campaigns.detail.reopenConfirm")
                : t("campaigns.detail.endConfirm");
              if (confirm(msg)) onPatch({ ended: !c.is_ended });
            }}
            disabled={patching}
          >
            {c.is_ended
              ? t("campaigns.detail.reopen")
              : t("campaigns.detail.endCampaign")}
          </button>
        </section>
      </PermissionGate>

      {/* Channels + reminder schedule. Inherits the org's until edited,
          so an untouched campaign shows the org's rows with a badge
          rather than an empty form. */}
      <PermissionGate perm="campaign.config">
        <section className="card space-y-3">
          <NotifyPolicyEditor scope={{ kind: "campaign", id: c.id }} />
        </section>
      </PermissionGate>

      {/* When the booking wave goes out, on this campaign's clock.
          Inherits the org's until edited. */}
      <PermissionGate perm="campaign.config">
        <section className="card space-y-3">
          <WaveScheduleEditor scope={{ kind: "campaign", id: c.id }} />
        </section>
      </PermissionGate>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Operator drill-down row (points #11, #12)                          */
/* ------------------------------------------------------------------ */

type OperatorStat = {
  user_id: number;
  display_name: string;
  email: string;
  /** Every non-cancelled session assigned. NOT a count of
   *  appointments — most of them may be invitations awaiting a slot. */
  booked: number;
  upcoming: number;
  /** Performed and closed. */
  done?: number;
  live?: number;
  missed?: number;
  /** Invited, no slot chosen yet. */
  awaiting?: number;
};


function OperatorCard({
  campaignId,
  op,
  totalBooked,
  onRemove,
}: {
  campaignId: string;
  op: OperatorStat;
  totalBooked: number;
  onRemove?: () => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { done, upcoming, awaiting, completionPct, sharePct } =
    operatorFigures(op, totalBooked);
  const initials = operatorInitials(op.display_name);
  return (
    <li className="flex flex-col gap-3 rounded-lg border border-nb-border bg-nb-surface p-3">
      <div className="flex items-center gap-2.5">
        <span
          aria-hidden="true"
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-nb-surface-2 text-xs font-semibold uppercase text-nb-text-2 ring-1 ring-nb-border"
        >
          {initials}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate font-semibold">{op.display_name}</span>
          {op.email && (
            <span className="block truncate text-xs text-nb-text-3">
              {op.email}
            </span>
          )}
        </span>
        {onRemove && (
          <PermissionGate perm="campaign.members.manage">
            <button
              type="button"
              className="btn-ghost shrink-0 text-xs text-nb-text-3 hover:text-cvp-danger"
              onClick={onRemove}
              aria-label={t("campaigns.detail.removeOperator", {
                name: op.display_name,
              })}
            >
              {t("common.remove", "Remove")}
            </button>
          </PermissionGate>
        )}
      </div>

      <div className="grid grid-cols-3 gap-1.5 text-center">
        <Figure
          label={t("campaigns.detail.opDone")}
          value={done}
          tone="text-emerald-400"
        />
        <Figure
          label={t("campaigns.detail.colUpcoming")}
          value={upcoming}
          tone="text-sky-400"
        />
        <Figure label={t("campaigns.detail.opAwaiting")} value={awaiting} />
      </div>

      {/* Two bars, each NAMED. The single bar here used to be filled to
          completion while the number beside it reported share — two
          different measures reading as one. It went unseen because the
          busiest operator's share and completion were both 98%. */}
      <Meter
        label={t("campaigns.detail.shareOfCampaign")}
        pct={sharePct}
        className="bg-sky-500"
      />
      <Meter
        label={t("campaigns.detail.completedLabel")}
        pct={completionPct}
        className="bg-nb-mint"
      />

      <button
        type="button"
        className="flex items-center gap-1.5 border-t border-nb-border pt-2.5 text-left text-xs font-medium text-nb-mint"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span aria-hidden>{open ? "▾" : "▸"}</span>
        {t("campaigns.detail.viewBeneficiaries")}
      </button>

      {open && (
        <div className="border-l border-nb-border/60 pl-3">
          <OperatorBeneficiaries campaignId={campaignId} userId={op.user_id} />
        </div>
      )}

    </li>
  );
}

